import { EmptyState, LineChart, type LineSeries, Skeleton } from '@/components';
import type { SamplePoint } from '@uchat-team-management/shared';
import { type JSX, Show } from 'solid-js';
import { bytes, dateTime, perSecond, timeOnly } from './format';
import { type SampleWindow, useMachineSamples } from './hooks';

const BLUE = '#2563eb';
const GREEN = '#059669';
const AMBER = '#d97706';
const VIOLET = '#7c3aed';

/** Points spaced more than 2.5 buckets apart get a null between them: an offline gap, not a line. */
export const withGaps = (
  points: SamplePoint[],
  bucketSeconds: number,
  pick: (p: SamplePoint) => number,
): LineSeries['points'] => {
  const out: LineSeries['points'] = [];
  let prev: number | undefined;
  for (const p of points) {
    const x = Date.parse(p.ts);
    if (prev !== undefined && x - prev > bucketSeconds * 2500) out.push({ x: prev + 1, y: null });
    out.push({ x, y: pick(p) });
    prev = x;
  }
  return out;
};

const Panel = (props: { title: string; children: JSX.Element }) => (
  <div class="rounded-xl border border-neutral-200 bg-surface p-4 dark:border-neutral-800 dark:bg-surface-dark">
    <h3 class="mb-2 text-sm font-medium text-neutral-600 dark:text-neutral-300">{props.title}</h3>
    {props.children}
  </div>
);

/**
 * Machine-level history. Every runner on the machine shares these numbers —
 * they are never a per-job measurement.
 */
export const ResourceCharts = (props: { machineId: string; window: SampleWindow }) => {
  const q = useMachineSamples(
    () => props.machineId,
    () => props.window,
  );
  const points = () => q.data?.data ?? [];
  const bucket = () => q.data?.meta.bucketSeconds ?? 15;
  const series = (
    label: string,
    color: string,
    pick: (p: SamplePoint) => number,
    fill = false,
  ) => ({
    label,
    color,
    fill,
    points: withGaps(points(), bucket(), pick),
  });
  const spanMs = () =>
    q.data ? Date.parse(q.data.meta.to) - Date.parse(q.data.meta.from) : 60 * 60_000;
  const formatX = (ms: number) => {
    const iso = new Date(ms).toISOString();
    return spanMs() > 24 * 60 * 60_000 ? dateTime(iso) : timeOnly(iso);
  };
  const memTotal = () => points().at(-1)?.memTotalBytes;
  const swapTotal = () => points().at(-1)?.swapTotalBytes;

  return (
    <Show when={!q.isPending} fallback={<Skeleton class="h-64" />}>
      <Show
        when={!q.isError}
        fallback={<EmptyState variant="error" title="Failed to load resource history" />}
      >
        <Show
          when={points().length > 0}
          fallback={
            <EmptyState
              title="No samples in this period"
              body="The agent sent no data for this window."
            />
          }
        >
          <div
            class="grid gap-4 transition-opacity duration-200 motion-reduce:transition-none md:grid-cols-2"
            classList={{ 'opacity-60': q.isFetching && q.isPlaceholderData }}
            aria-busy={q.isFetching}
          >
            <Panel title="CPU">
              <LineChart
                label="CPU percent over time"
                series={[series('CPU', BLUE, (p) => p.cpuPct, true)]}
                formatY={(v) => `${Math.round(v)}%`}
                formatX={formatX}
                yMax={100}
              />
            </Panel>
            <Panel title={`RAM${memTotal() ? ` (of ${bytes(memTotal())})` : ''}`}>
              <LineChart
                label="Memory used over time"
                series={[series('Used', GREEN, (p) => p.memUsedBytes, true)]}
                formatY={bytes}
                formatX={formatX}
                yMax={memTotal()}
              />
            </Panel>
            <Panel title={`Swap${swapTotal() ? ` (of ${bytes(swapTotal())})` : ''}`}>
              <LineChart
                label="Swap used over time"
                series={[series('Used', AMBER, (p) => p.swapUsedBytes, true)]}
                formatY={bytes}
                formatX={formatX}
              />
            </Panel>
            <Panel title="Disk read / write">
              <LineChart
                label="Disk throughput over time"
                series={[
                  series('Read', BLUE, (p) => p.diskReadBps),
                  series('Write', VIOLET, (p) => p.diskWriteBps),
                ]}
                formatY={perSecond}
                formatX={formatX}
              />
            </Panel>
            <Panel title="Network receive / send">
              <LineChart
                label="Network throughput over time"
                series={[
                  series('Receive', GREEN, (p) => p.netRxBps),
                  series('Send', AMBER, (p) => p.netTxBps),
                ]}
                formatY={perSecond}
                formatX={formatX}
              />
            </Panel>
          </div>
        </Show>
      </Show>
    </Show>
  );
};
